import { useEffect, useState } from 'react';
import {
  Page,
  Card,
  BlockStack,
  Text,
  Button,
  Banner,
  ProgressBar,
  Select,
  InlineStack,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../providers/ApiProvider';
import { openShopifyAdminUrl } from '../lib/shopifyNav';
import { themeEmbedDeepLink } from '../lib/shopContext';
import { t } from '../i18n';

export function OnboardingPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [merchantType, setMerchantType] = useState('general');
  const [themeDeepLink, setThemeDeepLink] = useState<string | null>(null);
  const [shopDomain, setShopDomain] = useState('');
  const [embedActive, setEmbedActive] = useState(false);

  const steps = [
    'Welcome to Freebi',
    'Tell us about your store',
    'Create your first campaign',
    'Enable theme widgets',
  ];

  useEffect(() => {
    void api
      .get<{
        shopDomain?: string;
        appEmbed?: { deepLink?: string; active?: boolean };
      }>('/stores/setup')
      .then((s) => {
        setShopDomain(s.shopDomain || '');
        setThemeDeepLink(
          themeEmbedDeepLink(s.appEmbed?.deepLink, s.shopDomain) || null,
        );
        setEmbedActive(Boolean(s.appEmbed?.active));
      })
      .catch(() => undefined);
  }, [api]);

  async function finish() {
    await api
      .patch('/stores/me', { onboardingDone: true, settings: { merchantType } })
      .catch(() => undefined);
    navigate('/');
  }

  function openThemeEditor() {
    const url =
      themeDeepLink || themeEmbedDeepLink(themeDeepLink, shopDomain);
    if (url) openShopifyAdminUrl(url, { newTab: true });
  }

  return (
    <Page title={t("Get started")} subtitle={t("Freebi is free — setup takes about 2 minutes")}>
      <BlockStack gap="400">
        <Banner tone="success">
          {t("No payment required. Full access to free gifts, BOGO, cart goals, and analytics.")}
        </Banner>
        <Card>
          <BlockStack gap="400">
            <Text as="p" tone="subdued">
              {t('Step {{current}} of {{total}}: {{name}}', {
                current: step + 1,
                total: steps.length,
                name: t(steps[step]),
              })}
            </Text>
            <ProgressBar progress={((step + 1) / steps.length) * 100} />

            {step === 0 && (
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  {t("Welcome to Freebi")}
                </Text>
                <Text as="p">
                  {t("Increase average order value with free gifts, BOGO, volume discounts, and cart\n                  goals — completely free.")}
                </Text>
                <Button variant="primary" onClick={() => setStep(1)}>
                  {t("Continue")}
                </Button>
              </BlockStack>
            )}

            {step === 1 && (
              <BlockStack gap="200">
                <Select
                  label={t("What do you sell?")}
                  options={[
                    { label: t("Fashion"), value: 'fashion' },
                    { label: t("Beauty"), value: 'beauty' },
                    { label: t("Electronics"), value: 'electronics' },
                    { label: t("General merchandise"), value: 'general' },
                  ]}
                  value={merchantType}
                  onChange={setMerchantType}
                />
                <Button variant="primary" onClick={() => setStep(2)}>
                  {t("Continue")}
                </Button>
              </BlockStack>
            )}

            {step === 2 && (
              <BlockStack gap="200">
                <Text as="p">
                  {t("Create a free gift, BOGO, or cart goal campaign for")} {merchantType} {t("stores.")}
                </Text>
                <Button onClick={() => navigate('/campaigns/new')}>{t("Create campaign")}</Button>
                <Button variant="primary" onClick={() => setStep(3)}>
                  {t("I&apos;ll do this later")}
                </Button>
              </BlockStack>
            )}

            {step === 3 && (
              <BlockStack gap="200">
                <Text as="p">
                  {t("Enable the Freebi storefront app embed so customers see gifts and progress on\n                  your Online Store. No Liquid or theme code edits required.")}
                </Text>
                {embedActive ? (
                  <Banner tone="success">{t("Storefront widget is already active.")}</Banner>
                ) : (
                  <Banner tone="info">
                    {t("Opens the theme editor. Turn on")} <strong>{t("Storefront gifts")}</strong> {t("(app embed),\n                    then click Save.")}
                  </Banner>
                )}
                <InlineStack gap="200">
                  <Button
                    variant="primary"
                    onClick={openThemeEditor}
                    disabled={
                      !themeEmbedDeepLink(themeDeepLink, shopDomain)
                    }
                  >
                    {embedActive ? 'Open theme editor' : 'Activate app embed'}
                  </Button>
                  <Button onClick={finish}>{t("Finish setup")}</Button>
                </InlineStack>
              </BlockStack>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
